import axios, { InternalAxiosRequestConfig } from 'axios';
import { ErrorTypes, GenericError } from 'models';
import {
  getToken,
  refreshAccessToken,
  getRefreshInflight,
} from '../../services/auth-service';
import { env } from 'env';
import { localStorageClient } from 'clients/local-storage-client';
import { sessionClientHeaders } from 'auth/session-client';

const baseUrl = env.REACT_APP_API_URL || '/api';

const instance = axios.create({
  baseURL: `${baseUrl}/`,
  // Needed here too, not just on the auth client: the persona switch goes through this instance
  // and opens a session. Without the header that session is stored with no owner, and a
  // "close my other sessions" in ToxAccess would be free to close it.
  headers: sessionClientHeaders,
});

export enum ApiErrorTypes {
  NetworkError = 'NETWORK_ERROR',
  NotFound = 'NOT_FOUND',
  Forbidden = 'FORBIDDEN',
  InvalidCredentials = 'INVALID_CREDENTIALS',
}

export async function appendAuthToken(
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
  let authToken = await getToken();

  // If the access token isn't in memory yet but a refresh is already in
  // flight (e.g. checkSession running on page load), wait for it to finish
  // before sending the request. This prevents 401s on startup API calls.
  if (!authToken) {
    const inflight = getRefreshInflight();
    if (inflight) {
      await inflight;
      authToken = await getToken();
    }
  }

  await localStorageClient.setItem<Date>('auth:lastApiCall', new Date());

  return {
    ...config,
    headers: {
      ...config.headers,
      ...(!!authToken && {
        Authorization: `Bearer ${authToken}`,
      }),
    },
  } as InternalAxiosRequestConfig;
}

export function rejectApiError(error: any) {
  const { message, response, config: { url = null } = {} } = error;

  const { status = null, data } = response || {};

  let returnError: GenericError;

  if (!response && message === 'Network Error') {
    // network error
    returnError = new GenericError(
      ApiErrorTypes.NetworkError,
      data?.message ?? message,
      response,
    );
  } else if (!!status && [401, 404, 403].includes(status)) {
    const mapping: Record<number, string> = {
      401: ApiErrorTypes.InvalidCredentials,
      403: ApiErrorTypes.Forbidden,
      404: ApiErrorTypes.NotFound,
    };
    returnError = new GenericError(
      mapping[status],
      data?.message ?? message,
      response,
    );
  } else if (!!data && !!('code' in data)) {
    const { code, message } = data;
    returnError = new GenericError(code, message, response);
  } else {
    returnError = new GenericError(
      ErrorTypes.Unknown,
      data?.message ?? message,
      response,
    );
  }

  return Promise.reject(returnError);
}

instance.interceptors.request.use(async (config) => {
  return await appendAuthToken(config);
});

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return instance(originalRequest);
      }
    }
    return rejectApiError(error);
  },
);
export const apiClient = instance;
